import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { companyInfo, navLinks, distributor } from '../../data/mock';
import { useMotion } from '../../context/MotionContext';
import GlitchText from '../ui/GlitchText';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { prefersReducedMotion } = useMotion();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header 
        className={`fixed left-0 right-0 transition-all duration-300 ${
          isScrolled ? 'py-3 bg-black/60' : 'py-4 bg-black/95'
        } backdrop-blur-sm border-b border-white/10`}
        style={{ zIndex: 1000, top: '36px' }}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo with Philippines indicator */}
          <Link to="/" className="flex items-center gap-3" aria-label="XION Medical Philippines">
            <div className="flex flex-col items-start">
              <img 
                src={companyInfo.logoUrl} 
                alt="XION Medical" 
                className="h-8 w-auto"
              />
              <span className="text-[10px] font-mono tracking-[0.2em] uppercase mt-0.5" aria-label="Philippines">
                {'Philippines'.split('').map((ch, i) => (
                  <span key={i} style={{ color: ['#0038A8', '#CE1126', '#FCD116', '#FFFFFF'][i % 4] }}>{ch}</span>
                ))}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.href.replace(/\//g, '') || 'home'}`}
                className={`relative font-mono text-sm tracking-wide transition-colors ${
                  location.pathname === link.href
                    ? 'text-[#00FFD1]'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                {prefersReducedMotion ? link.name : <GlitchText text={link.name} />}
              </Link>
            ))}
          </nav>

          {/* Right side: CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={distributor.website}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-contact-distributor-btn"
              className="btn-distributor text-sm"
            >
              Contact PH Distributor
              <ExternalLink size={14} />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black lg:hidden"
            style={{ zIndex: 999, paddingTop: '108px' }}
          >
            <nav className="flex flex-col h-full overflow-y-auto p-6" aria-label="Mobile navigation">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={link.href}
                    className={`block font-mono text-xl tracking-wide py-4 border-b border-white/10 ${
                      location.pathname === link.href
                        ? 'text-[#00FFD1]'
                        : 'text-white/80 hover:text-[#00FFD1]'
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              
              {/* Mobile CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-6"
              >
                <a
                  href={distributor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-contact-distributor-btn"
                  className="btn-distributor w-full justify-center"
                >
                  Contact PH Distributor
                  <ExternalLink size={16} />
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
